import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Trash2, Plus, FileText, Phone, Mail, Linkedin, Calendar, CheckSquare } from 'lucide-react';
import type { Deal, Contact, Activity, Task, PipelineStage, TeamMember, Team, ActivityType, TaskPriority } from '../../types';
import { ACTIVITY_TYPES, TASK_PRIORITIES } from '../../constants';
import { createDeal, updateDeal, deleteDeal, type CreateDealInput } from '../../services/deals';
import { getActivitiesForDeal, createActivity } from '../../services/activities';
import { getTasksForDeal, createTask, markTaskDone, markTaskOpen } from '../../services/tasks';
import { getContacts } from '../../services/contacts';

interface DealDrawerProps {
  deal: Deal | null;
  initialStage?: string;
  team: Team;
  stages: PipelineStage[];
  members: TeamMember[];
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

const ActivityIcon: React.FC<{ type: ActivityType }> = ({ type }) => {
  switch (type) {
    case 'call': return <Phone size={14} />;
    case 'email': return <Mail size={14} />;
    case 'linkedin': return <Linkedin size={14} />;
    default: return <FileText size={14} />;
  }
};

const DealDrawer: React.FC<DealDrawerProps> = ({
  deal,
  initialStage,
  team,
  stages,
  members,
  userId,
  onClose,
  onSaved,
}) => {
  const isEdit = !!deal;

  const [formData, setFormData] = useState({
    title: '',
    stage: initialStage || 'new',
    contact_id: '',
    owner_user_id: userId,
    value_monthly: '',
    value_credits_est: '',
    close_date: '',
    next_follow_up: '',
  });

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newActivity, setNewActivity] = useState({ type: 'note' as ActivityType, body: '' });
  const [addingActivity, setAddingActivity] = useState(false);

  const [newTask, setNewTask] = useState({ title: '', due_at: '', priority: 'medium' as TaskPriority });
  const [addingTask, setAddingTask] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const fetchRelatedData = useCallback(async () => {
    const contactsData = await getContacts(team.id);
    setContacts(contactsData);

    if (deal) {
      const [activitiesData, tasksData] = await Promise.all([
        getActivitiesForDeal(deal.id),
        getTasksForDeal(deal.id),
      ]);
      setActivities(activitiesData);
      setTasks(tasksData);
    }
  }, [team.id, deal]);

  useEffect(() => {
    fetchRelatedData();
  }, [fetchRelatedData]);

  useEffect(() => {
    if (deal) {
      setFormData({
        title: deal.title,
        stage: deal.stage,
        contact_id: deal.contact_id || '',
        owner_user_id: deal.owner_user_id || userId,
        value_monthly: deal.value_monthly?.toString() || '',
        value_credits_est: deal.value_credits_est?.toString() || '',
        close_date: deal.close_date || '',
        next_follow_up: deal.next_follow_up ? deal.next_follow_up.slice(0, 16) : '',
      });
    }
  }, [deal, userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    const input: CreateDealInput = {
      team_id: team.id,
      title: formData.title.trim(),
      stage: formData.stage,
      contact_id: formData.contact_id || null,
      owner_user_id: formData.owner_user_id || null,
      value_monthly: formData.value_monthly ? parseFloat(formData.value_monthly) : null,
      value_credits_est: formData.value_credits_est ? parseFloat(formData.value_credits_est) : null,
      close_date: formData.close_date || null,
      next_follow_up: formData.next_follow_up ? new Date(formData.next_follow_up).toISOString() : null,
    };

    let result;
    if (isEdit && deal) {
      result = await updateDeal({ id: deal.id, ...input });
    } else {
      result = await createDeal(input);
    }

    setSaving(false);

    if (result) {
      onSaved();
      if (!isEdit) onClose();
    } else {
      setError('Failed to save deal');
    }
  };

  const handleDelete = async () => {
    if (!deal || !confirm('Are you sure you want to delete this deal?')) return;

    setDeleting(true);
    const success = await deleteDeal(deal.id);
    setDeleting(false);

    if (success) {
      onSaved();
      onClose();
    } else {
      setError('Failed to delete deal');
    }
  };

  const handleAddActivity = async () => {
    if (!deal || !newActivity.body.trim()) return;

    setAddingActivity(true);
    const result = await createActivity({
      team_id: team.id,
      deal_id: deal.id,
      contact_id: deal.contact_id,
      owner_user_id: userId,
      type: newActivity.type,
      body: newActivity.body.trim(),
    });

    if (result) {
      setActivities([result, ...activities]);
      setNewActivity({ type: 'note', body: '' });
    }
    setAddingActivity(false);
  };

  const handleAddTask = async () => {
    if (!deal || !newTask.title.trim() || !newTask.due_at) return;

    setAddingTask(true);
    const result = await createTask({
      team_id: team.id,
      deal_id: deal.id,
      contact_id: deal.contact_id,
      owner_user_id: userId,
      title: newTask.title.trim(),
      due_at: new Date(newTask.due_at).toISOString(),
      priority: newTask.priority,
    });

    if (result) {
      setTasks([...tasks, result]);
      setNewTask({ title: '', due_at: '', priority: 'medium' });
      setShowTaskForm(false);
    }
    setAddingTask(false);
  };

  const handleToggleTask = async (task: Task) => {
    if (task.status === 'done') {
      await markTaskOpen(task.id);
      setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: 'open' as const } : t)));
    } else {
      await markTaskDone(task.id);
      setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: 'done' as const } : t)));
    }
  };

  const selectedContact = contacts.find((c) => c.id === formData.contact_id);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-slate-900 border-l border-slate-700 overflow-y-auto animate-fadeIn">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? 'Deal Details' : 'New Deal'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. ReHub for Acme Properties"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {stages.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Contact</label>
              <select
                value={formData.contact_id}
                onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">No contact</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} {c.company ? `(${c.company})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Monthly Value (£)</label>
              <input
                type="number"
                value={formData.value_monthly}
                onChange={(e) => setFormData({ ...formData, value_monthly: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Est. Credits/mo</label>
              <input
                type="number"
                value={formData.value_credits_est}
                onChange={(e) => setFormData({ ...formData, value_credits_est: e.target.value })}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Close Date</label>
              <input
                type="date"
                value={formData.close_date}
                onChange={(e) => setFormData({ ...formData, close_date: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Next Follow-up</label>
              <input
                type="datetime-local"
                value={formData.next_follow_up}
                onChange={(e) => setFormData({ ...formData, next_follow_up: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Owner</label>
            <select
              value={formData.owner_user_id}
              onChange={(e) => setFormData({ ...formData, owner_user_id: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.email || m.user_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Deal'}
            </button>
          </div>
        </form>

        {isEdit && deal && (
          <>
            {/* Tasks Section */}
            <div className="p-6 border-t border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-white flex items-center gap-2">
                  <CheckSquare size={16} />
                  Tasks
                </h3>
                <button
                  onClick={() => setShowTaskForm(!showTaskForm)}
                  className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300"
                >
                  <Plus size={14} />
                  Add Task
                </button>
              </div>

              {showTaskForm && (
                <div className="mb-4 p-3 bg-slate-800 rounded-lg space-y-3">
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Task title"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <div className="flex gap-2">
                    <input
                      type="datetime-local"
                      value={newTask.due_at}
                      onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })}
                      className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as TaskPriority })}
                      className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {TASK_PRIORITIES.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleAddTask}
                    disabled={addingTask || !newTask.title.trim() || !newTask.due_at}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {addingTask && <Loader2 size={14} className="animate-spin" />}
                    Add Task
                  </button>
                </div>
              )}

              <div className="space-y-2">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-center gap-3 p-2 rounded-lg ${task.status === 'done' ? 'bg-slate-800/50' : 'bg-slate-800'}`}
                  >
                    <button
                      onClick={() => handleToggleTask(task)}
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        task.status === 'done'
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      {task.status === 'done' && <span className="text-xs">✓</span>}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${task.status === 'done' ? 'text-slate-500 line-through' : 'text-white'}`}>
                        {task.title}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(task.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No tasks yet</p>
                )}
              </div>
            </div>

            {/* Activities Section */}
            <div className="p-6 border-t border-slate-700">
              <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                <Calendar size={16} />
                Activity
              </h3>

              <div className="mb-4 p-3 bg-slate-800 rounded-lg space-y-3">
                <div className="flex gap-2">
                  {ACTIVITY_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setNewActivity({ ...newActivity, type: t.value })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        newActivity.type === t.value
                          ? 'bg-orange-600 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      <ActivityIcon type={t.value} />
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={newActivity.body}
                  onChange={(e) => setNewActivity({ ...newActivity, body: e.target.value })}
                  placeholder="Add a note, log a call, or record an email..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
                <button
                  onClick={handleAddActivity}
                  disabled={addingActivity || !newActivity.body.trim()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {addingActivity && <Loader2 size={14} className="animate-spin" />}
                  Add Activity
                </button>
              </div>

              <div className="space-y-3">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 shrink-0">
                      <ActivityIcon type={activity.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white whitespace-pre-wrap">{activity.body}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(activity.happened_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                {activities.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No activity yet</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DealDrawer;
